import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Chip,
  Collapse,
  TextField,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  List,
  ListItem,
  ListItemText,
  InputAdornment,
  CircularProgress,
  Checkbox,
  Tooltip,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  MenuItem,
} from '@mui/material';
import SearchableSelect from 'components/Forms/searchable/SearchableSelect';
import { priceTransform, formatNumber } from '../../../helpers/formPrice';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Info as InfoIcon,
  OpenInNew as OpenInNewIcon,
  SwapVert as SwapVertIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  ContentCopy as ContentCopyIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import OptionSortModal from '../../../components/PosMenu/OptionSortModal';
import { toast } from 'react-toastify';
import confirm from '../../../components/Confirm';
import {
  createModifierGroupOption,
  createItemModifierGroup,
  deleteModifierGroupOption,
  updateItem,
  createItem,
  deleteItem,
  setSelectedEntity,
} from '../../../redux/actions/posMenu';
import {
  getTranslationValue,
  ENTITY_TYPES,
  PRICE_TYPE_OPTIONS,
  IN_ORDER_VIEW_LAYOUT_OPTIONS,
} from '../../../constants/posMenu';
import FieldSavingIndicator from '../../../components/PosMenu/FieldSavingIndicator';
import PosProviderPriceOverridePopover from '../../../components/PosMenu/PosProviderPriceOverridePopover';
import { Virtuoso } from 'react-virtuoso';
import { AvailabilityControl } from 'components/Availability';
import { getAvailabilityStatus } from 'utils/soldOut';
import { toSoldOutPayload } from 'constants/availabilityStatus';
import moment from 'moment-timezone';

// Helper function to find all ancestor items (items that have the given item as an option)
const getItemAncestors = (itemId, menuMap) => {
  const ancestors = new Set();
  const visited = new Set();

  const findParents = (targetId) => {
    if (visited.has(targetId)) return;
    visited.add(targetId);

    // Check all items to see if any have this item as an option
    Object.values(menuMap?.items || {}).forEach((item) => {
      (item.modifierGroupIds || []).forEach((mgId) => {
        const mg = menuMap?.modifierGroups?.[mgId];
        if (mg?.optionIds?.includes(targetId)) {
          ancestors.add(item._id);
          findParents(item._id); // Recursively find parents of parents
        }
      });
    });
  };

  findParents(itemId);
  return ancestors;
};

const ModifierGroupOptionsManager = ({
  modifierGroup,
  menuMap,
  parentItem,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const internalMenuId = useSelector((state) => state.posMenu.internalMenuId);
  const currentBusiness = useSelector((state) => state.business?.currentBusiness);
  const businessTimezone = currentBusiness?.timezone?.timeZoneId;
  const PAGE_SIZE = 10;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [expandedOptions, setExpandedOptions] = useState({});
  const [selectedItems, setSelectedItems] = useState([]);
  const [displayCount, setDisplayCount] = useState(PAGE_SIZE);

  // State for inline editing
  const [editingValues, setEditingValues] = useState({});

  // State for field saving indicators (per itemId and field)
  const [fieldSavingStatus, setFieldSavingStatus] = useState({});
  const saveTimeoutsRef = useRef({});
  const dirtyFieldsRef = useRef(new Set());

  // State for search and pagination
  const [searchQuery, setSearchQuery] = useState('');

  // State for creating new item
  const [isCreatingNewItem, setIsCreatingNewItem] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [newItemDescription, setNewItemDescription] = useState('');
  const [newItemPrice, setNewItemPrice] = useState(0);
  const [newItemPriceType, setNewItemPriceType] = useState(null);
  const [newItemHideNameOnOrder, setNewItemHideNameOnOrder] = useState(false);
  const [newItemShowNestedLayout, setNewItemShowNestedLayout] =
    useState('default');
  const [newItemInOrderViewLayout, setNewItemInOrderViewLayout] =
    useState(null);

  // Track newly created items locally (to handle timing issues with Redux)
  const [locallyCreatedItems, setLocallyCreatedItems] = useState({});

  // State for sort modal
  const [sortModalOpen, setSortModalOpen] = useState(false);

  // Loading states for save operations
  const [isCreatingItem, setIsCreatingItem] = useState(false);
  const [isAddingOptions, setIsAddingOptions] = useState(false);
  const [duplicatingOptionId, setDuplicatingOptionId] = useState(null);

  // State for tracking which selected items should be preselected (only used when parentItem exists)
  const [preselectedItems, setPreselectedItems] = useState(new Set());

  // Get all items from menuMap
  const allItems = useMemo(
    () => (menuMap?.items ? Object.values(menuMap.items) : []),
    [menuMap?.items]
  );

  // Get options for this modifier group (memoized to prevent infinite loops)
  // Try both 'options' and 'optionIds' fields
  const modifierOptions = useMemo(() => {
    const rawOptions = modifierGroup?.optionIds || [];

    const mapped = Array.isArray(rawOptions)
      ? rawOptions
          .map((itemId, index) => {
            // optionIds is just an array of item IDs (strings)
            const itemData = menuMap?.items?.[itemId];
            if (!itemData) {
              return null;
            }

            // Sort is the position (index) in the optionIds array
            const sort = index;

            return {
              itemId,
              sort, // Position in optionIds array
              itemData,
            };
          })
          .filter(Boolean)
      : [];

    // Use the order from optionIds array directly (no sorting needed)
    // The optionIds array is maintained in correct order by the reducer
    return mapped;
  }, [modifierGroup?._id, modifierGroup?.optionIds, menuMap?.items]);

  // Get items to exclude (parentItem and its ancestors) to prevent circular references
  const itemsToExclude = useMemo(() => {
    const excludeSet = new Set();
    if (parentItem?._id) {
      excludeSet.add(parentItem._id);
      getItemAncestors(parentItem._id, menuMap).forEach((id) =>
        excludeSet.add(id)
      );
    }
    return excludeSet;
  }, [parentItem?._id, menuMap]);

  // Get items not yet in this modifier group (memoized)
  const availableItems = useMemo(
    () =>
      allItems.filter(
        (item) =>
          !modifierOptions.some(
            (option) =>
              option.itemId === item._id || option.itemData?._id === item._id
          ) && !itemsToExclude.has(item._id) // Exclude parent item and its ancestors
      ),
    [allItems, modifierOptions, itemsToExclude]
  );

  // Filter items based on search query (name or internal name)
  const filteredItems = useMemo(() => {
    if (!searchQuery) return availableItems;
    const query = searchQuery.toLowerCase();
    return availableItems.filter((item) => {
      const name = getTranslationValue(item.name).toLowerCase();
      const title = getTranslationValue(item.title)?.toLowerCase() || '';
      return name.includes(query) || title.includes(query);
    });
  }, [availableItems, searchQuery]);

  // Get selected items data for display at top
  // Check both menuMap.items and locallyCreatedItems to handle timing issues
  const selectedItemsData = useMemo(() => {
    return selectedItems
      .map((itemId) => {
        const fromMenuMap = menuMap?.items?.[itemId];
        const fromLocal = locallyCreatedItems[itemId];
        return fromMenuMap || fromLocal;
      })
      .filter(Boolean);
  }, [selectedItems, menuMap?.items, locallyCreatedItems]);

  // Get unselected filtered items (excluding selected ones)
  const unselectedFilteredItems = useMemo(() => {
    return filteredItems.filter((item) => !selectedItems.includes(item._id));
  }, [filteredItems, selectedItems]);

  // Paginated unselected items. Only a new search goes back to the first
  // page; selecting an item keeps the pages already loaded.
  useEffect(() => {
    setDisplayCount(PAGE_SIZE);
  }, [searchQuery]);

  const displayedItems = useMemo(
    () => unselectedFilteredItems.slice(0, displayCount),
    [unselectedFilteredItems, displayCount]
  );

  const hasMore = unselectedFilteredItems.length > displayedItems.length;

  const handleLoadMore = () => {
    setDisplayCount((count) => count + PAGE_SIZE);
  };

  // Helper function to clean preselectOptions by removing stale modifier groups and options
  // Preserves nested preselection composite keys (format: optionItemId_nestedModGroupId)
  const cleanPreselectOptions = (currentOptions) => {
    if (!currentOptions || !parentItem?.modifierGroupIds) {
      return {};
    }

    const cleanedOptions = {};
    const validModifierGroupIds = new Set(parentItem.modifierGroupIds);

    for (const [key, selections] of Object.entries(currentOptions)) {
      // Check if this is a composite key for nested preselections (contains underscore)
      // Composite keys have format: optionItemId_nestedModGroupId
      const isCompositeKey = key.includes('_');

      if (isCompositeKey) {
        // For composite keys (nested selections), preserve them as-is
        // These are managed by the NestedModifierSelector component
        if (selections && selections.length > 0) {
          cleanedOptions[key] = selections;
        }
        continue;
      }

      // For top-level modifier group keys, validate against item's modifier groups
      if (!validModifierGroupIds.has(key)) {
        continue;
      }

      const modGroup = menuMap?.modifierGroups?.[key];
      if (!modGroup) {
        continue;
      }

      // Get valid option IDs for this modifier group
      const validOptionIds = new Set(modGroup.optionIds || []);

      // Filter out options that are no longer in this modifier group
      const cleanedSelections = (selections || []).filter((sel) =>
        validOptionIds.has(sel._id)
      );

      // Only keep the modifier group entry if it has valid selections
      if (cleanedSelections.length > 0) {
        cleanedOptions[key] = cleanedSelections;
      }
    }

    return cleanedOptions;
  };

  const handleToggleExpand = (optionId) => {
    setExpandedOptions((prev) => ({
      ...prev,
      [optionId]: !prev[optionId],
    }));
  };

  const handleNavigateToItem = (itemId) => {
    // Check if we're in Overview flow by checking the current route
    const isOverviewFlow = location.pathname.includes('/pos-menu/overview');

    if (isOverviewFlow) {
      // Overview flow - select item in Overview
      const item = menuMap?.items?.[itemId];
      if (item) {
        dispatch(setSelectedEntity(item, ENTITY_TYPES.ITEM));
        // Navigate to Overview page if not already there
        if (!location.pathname.includes('/pos-menu/overview')) {
          history.push(`/pos-menu/overview?item=${itemId}`);
        }
      }
    } else {
      // Separate flow (Items or ModifierGroups) - navigate to Items route
      // Build navigation path from existing path + current context
      const currentPath = location.pathname;
      const itemEditMatch = currentPath.match(/^\/pos-menu\/items\/(.+)$/);
      const mgEditMatch = currentPath.match(
        /^\/pos-menu\/modifier-groups\/(.+)$/
      );
      const currentItemId =
        itemEditMatch && itemEditMatch[1] !== 'new' ? itemEditMatch[1] : null;
      const currentMgId =
        mgEditMatch && mgEditMatch[1] !== 'new' ? mgEditMatch[1] : null;

      // Get existing navigation path from location state
      const existingPath = location.state?.navigationPath || [];

      // Build new path: add current item or modifier group to path
      const newPath = [...existingPath];
      if (currentItemId && menuMap?.items?.[currentItemId]) {
        newPath.push({
          id: currentItemId,
          type: 'item',
          name:
            getTranslationValue(menuMap.items[currentItemId].name) || 'Item',
        });
      } else if (currentMgId && menuMap?.modifierGroups?.[currentMgId]) {
        newPath.push({
          id: currentMgId,
          type: 'modifierGroup',
          name:
            getTranslationValue(menuMap.modifierGroups[currentMgId].name) ||
            'Modifier Group',
        });
      }

      history.push({
        pathname: `/pos-menu/items/${itemId}`,
        state: {
          navigationPath: newPath,
        },
      });
    }
  };

  // Initialize editing values when modifier group changes or options are added
  useEffect(() => {
    setEditingValues((prev) => {
      const newValues = { ...prev };
      modifierOptions.forEach((option) => {
        // Only initialize if not already set (preserve user edits)
        if (!newValues[option.itemId]) {
          newValues[option.itemId] = {
            name: getTranslationValue(option.itemData?.name) || '',
            price: option.itemData?.price,
            priceType: option.itemData?.priceType || null,
            inOrderViewLayout: option.itemData?.inOrderViewLayout || null,
          };
        }
      });
      return newValues;
    });
  }, [modifierGroup?._id, modifierOptions.length]);

  // Sync editing values with Redux when option data changes externally
  // This ensures that if another component updates an item, we see the fresh data
  useEffect(() => {
    if (!modifierOptions.length) return;

    setEditingValues((prev) => {
      let hasChanges = false;
      const newValues = { ...prev };

      modifierOptions.forEach((option) => {
        const currentVal = prev[option.itemId];
        if (!currentVal) return;

        const reduxValues = {
          name: getTranslationValue(option.itemData?.name) || '',
          price: option.itemData?.price,
          priceType: option.itemData?.priceType || null,
          inOrderViewLayout: option.itemData?.inOrderViewLayout || null,
        };

        // A field the user has edited but not yet saved, or one that is
        // currently saving, keeps its local value.
        const nextVal = { ...currentVal };
        let rowChanged = false;
        Object.keys(reduxValues).forEach((field) => {
          const key = `${option.itemId}_${field}`;
          if (
            dirtyFieldsRef.current.has(key) ||
            fieldSavingStatus[key] === 'saving'
          ) {
            return;
          }
          if (currentVal[field] !== reduxValues[field]) {
            nextVal[field] = reduxValues[field];
            rowChanged = true;
          }
        });

        if (rowChanged) {
          hasChanges = true;
          newValues[option.itemId] = nextVal;
        }
      });

      return hasChanges ? newValues : prev;
    });
  }, [modifierOptions, fieldSavingStatus]);

  const handleFieldChange = (itemId, field, value, currentValues) => {
    dirtyFieldsRef.current.add(`${itemId}_${field}`);
    setEditingValues((prev) => {
      // Use currentValues passed from the render (guaranteed to be current)
      // as fallback if this option wasn't initialized in editingValues
      const existingValues = prev[itemId] || currentValues || {};

      return {
        ...prev,
        [itemId]: {
          ...existingValues, // Start with existing/current values
          [field]: value, // Apply the new value
        },
      };
    });
  };

  const setFieldStatus = (itemId, field, status) => {
    const key = `${itemId}_${field}`;
    setFieldSavingStatus((prev) => ({
      ...prev,
      [key]: status,
    }));

    // Auto-clear 'saved' status after 2 seconds
    if (status === 'saved') {
      if (saveTimeoutsRef.current[key]) {
        clearTimeout(saveTimeoutsRef.current[key]);
      }
      saveTimeoutsRef.current[key] = setTimeout(() => {
        setFieldSavingStatus((prev) => ({
          ...prev,
          [key]: null,
        }));
      }, 2000);
    }
  };

  // Cleanup timeouts on unmount AND when modifierGroup changes
  useEffect(() => {
    return () => {
      Object.values(saveTimeoutsRef.current).forEach(clearTimeout);
      // Clear all pending timeouts when modifier group changes
      saveTimeoutsRef.current = {};
      dirtyFieldsRef.current = new Set();
    };
  }, [modifierGroup?._id]);

  const handleBlurSave = async (option, field) => {
    const dirtyKey = `${option.itemId}_${field}`;
    try {
      const newValues = editingValues[option.itemId];
      dirtyFieldsRef.current.delete(dirtyKey);

      if (!newValues) {
        return;
      }

      const originalName = getTranslationValue(option.itemData?.name);
      const originalPrice = option.itemData?.price;
      const originalPriceType = option.itemData?.priceType;
      const originalInOrderViewLayout = option.itemData?.inOrderViewLayout || null;

      const hasNameChanged = newValues.name !== originalName;
      const hasPriceChanged =
        parseFloat(newValues.price) !== parseFloat(originalPrice);
      const hasPriceTypeChanged = newValues.priceType !== originalPriceType;
      const hasInOrderViewLayoutChanged = newValues.inOrderViewLayout !== originalInOrderViewLayout;

      // Check if the specific field changed
      const fieldChanged =
        (field === 'name' && hasNameChanged) ||
        (field === 'price' && hasPriceChanged) ||
        (field === 'priceType' && hasPriceTypeChanged) ||
        (field === 'inOrderViewLayout' && hasInOrderViewLayoutChanged);

      if (!fieldChanged) {
        return; // Nothing changed for this field
      }

      // Show saving indicator
      setFieldStatus(option.itemId, field, 'saving');

      // Update item name and/or price using PATCH (partial update)
      const updatedItemData = {
        provider: 'pos', // Required by backend
      };

      // Update only the changed field
      if (field === 'name' && hasNameChanged) {
        updatedItemData.name = {
          ...option.itemData?.name,
          en: newValues.name,
        };
      } else if (field === 'price' && hasPriceChanged) {
        updatedItemData.price =
          newValues.price === '' || newValues.price == null
            ? null
            : parseFloat(newValues.price);
      } else if (field === 'priceType' && hasPriceTypeChanged) {
        updatedItemData.priceType = newValues.priceType || null;
      } else if (field === 'inOrderViewLayout' && hasInOrderViewLayoutChanged) {
        updatedItemData.inOrderViewLayout = newValues.inOrderViewLayout || null;
      }

      await dispatch(updateItem(option.itemId, updatedItemData));

      // Show success indicator
      setFieldStatus(option.itemId, field, 'saved');

      // Write back only the saved field, unless the user edited it again
      // while the request was in flight.
      if (!dirtyFieldsRef.current.has(dirtyKey)) {
        const savedValue =
          field === 'price'
            ? updatedItemData.price
            : field === 'name'
            ? newValues.name
            : updatedItemData[field];
        setEditingValues((prev) => ({
          ...prev,
          [option.itemId]: {
            ...prev[option.itemId],
            [field]: savedValue,
          },
        }));
      }
    } catch (error) {
      // Show error indicator
      setFieldStatus(option.itemId, field, 'error');

      // Handle validation errors (array of messages)
      let errorMessage = 'Failed to save changes';
      if (error.response?.data?.message) {
        if (Array.isArray(error.response.data.message)) {
          errorMessage = error.response.data.message.join(', ');
        } else {
          errorMessage = error.response.data.message;
        }
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(`Save failed: ${errorMessage}`);
    }
  };

  const handleOpenDialog = () => {
    setSelectedItems([]);
    setSearchQuery('');
    setDisplayCount(PAGE_SIZE);
    setDialogOpen(true);
    // Reset new item creation state
    setIsCreatingNewItem(false);
    setNewItemName('');
    setNewItemDescription('');
    setNewItemPrice(0);
    setNewItemPriceType(null);
    setNewItemHideNameOnOrder(false);
    setNewItemShowNestedLayout('default');
    setNewItemInOrderViewLayout(null);
    setLocallyCreatedItems({});
  };

  // Items made with "Create New Item" exist as soon as they're created. Any
  // that didn't end up as an option of this group are deleted when the dialog
  // closes, so cancelling doesn't leave orphan items behind.
  const discardUnattachedCreatedItems = async (createdItems, attachedIds) => {
    const keep = new Set([
      ...attachedIds,
      ...(menuMap?.modifierGroups?.[modifierGroup._id]?.optionIds || []),
    ]);
    const orphans = Object.values(createdItems).filter(
      (created) => !keep.has(created._id)
    );
    if (orphans.length === 0) return;
    const results = await Promise.allSettled(
      orphans.map((orphan) => dispatch(deleteItem(orphan._id)))
    );
    const failedNames = orphans
      .filter((_, i) => results[i].status === 'rejected')
      .map((orphan) => getTranslationValue(orphan.name));
    if (failedNames.length > 0) {
      toast.error(
        `Couldn't delete unused new item(s): ${failedNames.join(', ')}`
      );
    }
  };

  const handleCloseDialog = () => {
    discardUnattachedCreatedItems(locallyCreatedItems, []);
    resetDialog();
  };

  const resetDialog = () => {
    setDialogOpen(false);
    setSelectedItems([]);
    setPreselectedItems(new Set());
    setSearchQuery('');
    setDisplayCount(PAGE_SIZE);
    setIsCreatingNewItem(false);
    setNewItemName('');
    setNewItemDescription('');
    setNewItemPrice(0);
    setNewItemPriceType(null);
    setNewItemHideNameOnOrder(false);
    setNewItemShowNestedLayout('default');
    setNewItemInOrderViewLayout(null);
    setLocallyCreatedItems({});
  };

  const handleToggleItem = useCallback((itemId) => {
    setSelectedItems((prev) => {
      if (prev.includes(itemId)) {
        return prev.filter((id) => id !== itemId);
      }
      return [...prev, itemId];
    });
  }, []);

  const handleCreateNewItem = async () => {
    try {
      if (!newItemName.trim()) {
        toast.error('Item name is required');
        return;
      }

      setIsCreatingItem(true);

      // Create the new item
      // Title = option name + modifier group title
      const mgTitle =
        getTranslationValue(modifierGroup.title) ||
        getTranslationValue(modifierGroup.name) ||
        '';
      const newItem = await dispatch(
        createItem({
          name: { en: newItemName },
          title: { en: `${newItemName} ${mgTitle}`.trim() },
          description: { en: newItemDescription },
          price: parseFloat(newItemPrice) || 0,
          priceType: newItemPriceType,
          tax: null,
          sort: 0,
          tags: [],
          isOnePrice: false,
          image: { url: '', key: '' },
          soldOut: { status: 'available', soldOutDate: null },
          onItsOwn: false,
          isActive: true,
          isPopular: false,
          scheduleIds: [],
          printerTags: [],
          menu: internalMenuId,
          minAllowedQuantity: null,
          maxAllowedQuantity: null,
          minTotalNumberOfModifierGroups: null,
          maxTotalNumberOfModifierGroups: null,
          minTotalNumberOfModifierItems: null,
          maxTotalNumberOfModifierItems: null,
          hideNameOnOrder: newItemHideNameOnOrder,
          showNestedLayout: newItemShowNestedLayout,
          inOrderViewLayout: newItemInOrderViewLayout,
        })
      );

      // Add the new item to locallyCreatedItems for immediate display
      setLocallyCreatedItems((prev) => ({
        ...prev,
        [newItem._id]: newItem,
      }));

      // Add the new item to selectedItems (will be attached when user clicks "Add Options")
      setSelectedItems((prev) => [...prev, newItem._id]);

      toast.success(
        `Item "${newItemName}" created. Click "Add Options" to attach it.`
      );
      setIsCreatingNewItem(false);
      setNewItemName('');
      setNewItemDescription('');
      setNewItemPrice(0);
      setNewItemPriceType(null);
      setNewItemHideNameOnOrder(false);
      setNewItemShowNestedLayout('default');
      setNewItemInOrderViewLayout(null);
    } catch (error) {
      toast.error(error.message || 'An error occurred');
    } finally {
      setIsCreatingItem(false);
    }
  };

  const handleAddOptions = async () => {
    try {
      if (selectedItems.length === 0) {
        toast.error('Please select at least one item');
        return;
      }

      setIsAddingOptions(true);

      // Find the max sort value from backend to ensure new items are added at the end
      // Backend may have non-sequential values (0, 10, 20), so we need to use max + 1
      const maxBackendSort = modifierOptions.reduce(
        (max, opt) => Math.max(max, opt.itemData?.optionSort ?? 0),
        -1
      );
      // Use whichever is higher: array length or max backend sort + 1
      const baseSort = Math.max(modifierOptions.length, maxBackendSort + 1);

      for (let i = 0; i < selectedItems.length; i++) {
        // Use baseSort to ensure we're higher than all existing backend sort values
        const sortValue = baseSort + i;
        await dispatch(
          createModifierGroupOption({
            modifierGroupId: modifierGroup._id,
            optionId: selectedItems[i],
            menuId: internalMenuId, // FIX: Use internal menu ID for consistency
            sort: sortValue, // Add at the end with sort value higher than existing max
          })
        );
      }

      // If there are preselected items and a parent item, update the parent's preselectOptions
      if (preselectedItems.size > 0 && parentItem) {
        const currentPreselectOptions = parentItem.preselectOptions || {
          defaultSelection: false,
          options: {},
        };
        const currentModifierGroupSelections =
          currentPreselectOptions.options?.[modifierGroup._id] || [];

        // Check if this is radio mode (single selection only)
        const isRadioMode = modifierGroup.min === 1 && modifierGroup.max === 1;

        let newSelections;
        if (isRadioMode) {
          // Radio mode: replace existing selection with the last preselected item
          const lastPreselectedId = Array.from(preselectedItems).pop();
          newSelections = [{ _id: lastPreselectedId, count: 1, sticky: false }];
        } else {
          // Multi-select mode: add preselected items to existing selections
          newSelections = [...currentModifierGroupSelections];
          preselectedItems.forEach((itemId) => {
            // Only add if not already in selections
            if (!newSelections.some((sel) => sel._id === itemId)) {
              newSelections.push({ _id: itemId, count: 1, sticky: false });
            }
          });
          // Respect max selection limit if set
          if (modifierGroup.max && newSelections.length > modifierGroup.max) {
            newSelections = newSelections.slice(-modifierGroup.max);
          }
        }

        const cleanedOptions = cleanPreselectOptions(
          currentPreselectOptions.options
        );
        const newPreselectOptions = {
          defaultSelection: true, // Enable default selections
          options: {
            ...cleanedOptions,
            [modifierGroup._id]: newSelections,
          },
        };

        await dispatch(
          updateItem(parentItem._id, { preselectOptions: newPreselectOptions })
        );
      }

      const preselectedCount = preselectedItems.size;
      toast.success(
        `${selectedItems.length} option(s) added to modifier group${
          preselectedCount > 0 ? ` (${preselectedCount} preselected)` : ''
        }`
      );
      discardUnattachedCreatedItems(locallyCreatedItems, selectedItems);
      resetDialog();
    } catch (error) {
      toast.error(error.message || 'An error occurred');
    } finally {
      setIsAddingOptions(false);
    }
  };

  const handleOptionAvailabilityChange = async (option, availabilityStatus) => {
    try {
      await dispatch(
        updateItem(option.itemId, {
          soldOut: toSoldOutPayload(
            availabilityStatus,
            businessTimezone,
            moment
          ),
          provider: 'pos',
        })
      );
    } catch (error) {
      toast.error(error.message || 'Failed to update availability');
    }
  };

  const handleRemoveOption = async (option) => {
    const confirmed = await confirm({
      title: 'Remove Option',
      message: `Remove "${getTranslationValue(
        option.itemData?.name
      )}" from this modifier group?`,
      confirmText: 'Remove',
      cancelText: 'Cancel',
      confirmColor: 'error',
    });

    if (!confirmed) return;

    try {
      await dispatch(
        deleteModifierGroupOption({
          modifierGroupId: modifierGroup._id,
          optionId: option.itemId,
        })
      );

      // Clean up preselectOptions - remove this option from parent item's preselections if it exists
      if (parentItem) {
        const currentPreselectOptions = parentItem.preselectOptions;
        const modifierGroupSelections =
          currentPreselectOptions?.options?.[modifierGroup._id];

        if (modifierGroupSelections?.some((sel) => sel._id === option.itemId)) {
          const newSelections = modifierGroupSelections.filter(
            (sel) => sel._id !== option.itemId
          );

          // Clean up any stale modifier groups/options first
          let newOptions = cleanPreselectOptions(
            currentPreselectOptions?.options
          );

          // Update the current modifier group's selections
          if (newSelections.length > 0) {
            newOptions[modifierGroup._id] = newSelections;
          } else {
            // If no more options are selected for this modifier group, remove the key
            delete newOptions[modifierGroup._id];
          }

          // If no more options are preselected anywhere, disable defaultSelection
          const hasAnyOptions = Object.keys(newOptions).length > 0;
          const newPreselectOptions = {
            defaultSelection: hasAnyOptions
              ? currentPreselectOptions?.defaultSelection || false
              : false,
            options: newOptions,
          };

          await dispatch(
            updateItem(parentItem._id, {
              preselectOptions: newPreselectOptions,
            })
          );
        }
      }

      toast.success('Option removed successfully');
    } catch (error) {
      toast.error(error.message || 'An error occurred');
    }
  };

  const handleDuplicateOption = async (option) => {
    if (!internalMenuId) {
      toast.error('Menu information is not loaded. Please refresh the page.');
      return;
    }

    setDuplicatingOptionId(option.itemId);
    try {
      const originalItem = option.itemData;
      const originalName = getTranslationValue(originalItem?.name);

      // Create a duplicate of the item
      const duplicateData = {
        name: {
          en: `Copy of ${originalName}`,
          es: originalItem?.name?.es ? `Copia de ${originalItem.name.es}` : '',
        },
        description: originalItem?.description || { en: '', es: '' },
        title: originalItem?.title || { en: '', es: '' },
        price: originalItem?.price || 0,
        tax: originalItem?.tax,
        sort: 0,
        tags: originalItem?.tags || [],
        isOnePrice: originalItem?.isOnePrice || false,
        image: originalItem?.image || { url: '', fileId: '' },
        soldOut: { status: 'available', soldOutDate: null },
        onItsOwn: originalItem?.onItsOwn || false,
        isSizeOption: originalItem?.isSizeOption || false,
        kitchenInstruction: originalItem?.kitchenInstruction || {
          en: '',
          es: '',
        },
        recipe: originalItem?.recipe || { en: '', es: '' },
        minAllowedQuantity: originalItem?.minAllowedQuantity,
        maxAllowedQuantity: originalItem?.maxAllowedQuantity,
        minTotalNumberOfModifierGroups:
          originalItem?.minTotalNumberOfModifierGroups,
        maxTotalNumberOfModifierGroups:
          originalItem?.maxTotalNumberOfModifierGroups,
        minTotalNumberOfModifierItems:
          originalItem?.minTotalNumberOfModifierItems,
        maxTotalNumberOfModifierItems:
          originalItem?.maxTotalNumberOfModifierItems,
        isPopular: originalItem?.isPopular || false,
        scheduleIds: originalItem?.scheduleIds || [],
        printerTags: originalItem?.printerTags || [],
        menu: internalMenuId,
        hideNameOnOrder: originalItem?.hideNameOnOrder || false,
        showNestedLayout: originalItem?.showNestedLayout ?? null,
        inOrderViewLayout: originalItem?.inOrderViewLayout || null,
        priceType: originalItem?.priceType ?? null,
        availableInKiosk: originalItem?.availableInKiosk ?? false,
      };

      // Create the new item
      const newItem = await dispatch(createItem(duplicateData));

      // The copy keeps the source option's nested groups
      const nestedGroupIds = originalItem?.modifierGroupIds || [];
      let failedNestedGroups = 0;
      for (let i = 0; i < nestedGroupIds.length; i++) {
        try {
          await dispatch(
            createItemModifierGroup({
              itemId: newItem._id,
              modifierGroupId: nestedGroupIds[i],
              menuId: internalMenuId,
              sort: i,
            })
          );
        } catch (nestedError) {
          failedNestedGroups++;
        }
      }

      // Add the new item to this modifier group
      const maxBackendSort = modifierOptions.reduce(
        (max, opt) => Math.max(max, opt.itemData?.optionSort ?? 0),
        -1
      );
      const sortValue = Math.max(modifierOptions.length, maxBackendSort + 1);

      await dispatch(
        createModifierGroupOption({
          modifierGroupId: modifierGroup._id,
          optionId: newItem._id,
          menuId: internalMenuId,
          sort: sortValue,
        })
      );

      if (failedNestedGroups > 0) {
        toast.warning(
          `Option duplicated, but ${failedNestedGroups} of ${nestedGroupIds.length} nested modifier group(s) couldn't be copied`
        );
      } else {
        toast.success('Option duplicated successfully');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to duplicate option');
    } finally {
      setDuplicatingOptionId(null);
    }
  };

  const VirtuosoList = useMemo(() => {
    const Component = React.forwardRef((props, ref) => (
      <List
        ref={ref}
        disablePadding
        sx={{
          px: '10px',
          width: '100%',
        }}
        {...props}
      />
    ));
    Component.displayName = 'ModifierGroupAvailableItemsList';
    return Component;
  }, []);

  const VirtuosoScroller = useMemo(() => {
    const Component = React.forwardRef((props, ref) => (
      <div
        ref={ref}
        style={{
          overflowAnchor: 'none', // prevent browser scroll anchoring jumpiness
          overscrollBehavior: 'contain', // prevent scroll bounce
        }}
        {...props}
      />
    ));
    Component.displayName = 'ModifierGroupAvailableItemsScroller';
    return Component;
  }, []);

  const VirtuosoFooter = useMemo(() => {
    const Component = () => (
      <Box
        sx={{
          height: '8px',
          width: '100%',
          flexShrink: 0,
        }}
      />
    );
    Component.displayName = 'ModifierGroupAvailableItemsFooter';
    return Component;
  }, []);

  const virtuosoComponents = useMemo(
    () => ({
      List: VirtuosoList,
      Scroller: VirtuosoScroller,
      Footer: VirtuosoFooter,
    }),
    [VirtuosoList, VirtuosoScroller, VirtuosoFooter]
  );

  const renderAvailableItem = useCallback(
    (item) => {
      const isSelected = selectedItems.includes(item._id);
      const isPreselected = preselectedItems.has(item._id);

      const handlePreselect = (e) => {
        e.stopPropagation();
        setPreselectedItems((prev) => {
          const newSet = new Set(prev);
          if (newSet.has(item._id)) {
            newSet.delete(item._id);
          } else {
            newSet.add(item._id);
          }
          return newSet;
        });
      };

      return (
        <Box
          key={item._id}
          sx={{
            width: '100%',
          }}
        >
          <ListItem
            button
            onClick={() => handleToggleItem(item._id)}
            selected={isSelected}
            sx={{
              border: '1px solid',
              borderColor: isSelected ? 'primary.main' : 'divider',
              borderRadius: 1.5,
              mb: 1.5,
              bgcolor: isSelected ? '#e3f2fd' : 'white',
              width: '100%',
              boxSizing: 'border-box',
              '&:hover': {
                bgcolor: isSelected ? '#e3f2fd' : '#f5f5f5',
              },
            }}
          >
            <ListItemText
              primary={
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    mb: 0.5,
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                  }}
                >
                  {getTranslationValue(item.name)}
                  {getTranslationValue(item.title) && (
                    <Typography
                      component="span"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 400,
                        ml: 1,
                      }}
                    >
                      ({getTranslationValue(item.title)})
                    </Typography>
                  )}
                </Typography>
              }
              secondary={
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                  }}
                >
                  Base price: ${(Number(item.price) || 0).toFixed(2)}
                </Typography>
              }
            />
            {/* Show preselect checkbox only when item is selected and parentItem exists */}
            {isSelected && parentItem && (
              <Tooltip
                title={
                  isPreselected
                    ? 'Remove from preselected'
                    : 'Set as default preselected'
                }
              >
                <Checkbox
                  checked={isPreselected}
                  onClick={handlePreselect}
                  size="small"
                  icon={<StarBorderIcon />}
                  checkedIcon={<StarIcon sx={{ color: '#ffc107' }} />}
                  sx={{ ml: 1 }}
                  inputProps={{ 'aria-label': 'Preselect this option' }}
                />
              </Tooltip>
            )}
          </ListItem>
        </Box>
      );
    },
    [handleToggleItem, selectedItems, parentItem, preselectedItems]
  );

  return (
    <Box sx={{ mt: 2, pl: 2, borderLeft: '2px solid #e0e0e0' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
        }}
      >
        <Typography variant="subtitle2" color="textSecondary">
          Options ({modifierOptions.length})
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
          >
            Add Options
          </Button>
          {modifierOptions.length > 1 && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<SwapVertIcon />}
              onClick={() => setSortModalOpen(true)}
            >
              Sort
            </Button>
          )}
        </Box>
      </Box>

      {modifierOptions.length === 0 ? (
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{ fontStyle: 'italic' }}
        >
          No options added yet. Options are items that can be selected within
          this modifier group.
        </Typography>
      ) : (() => {
        const showOrderViewLayoutColumn =
          modifierGroup?.priceType === 'multiplierApplyingToParentPrice' ||
          modifierOptions.some(
            (opt) => opt.itemData?.priceType === 'multiplierApplyingToParentPrice'
          );
        return (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>
                  Price
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: 160 }}>
                  Price Type
                </TableCell>
                {showOrderViewLayoutColumn && (
                  <TableCell sx={{ fontWeight: 'bold', width: 160 }}>
                    Order View Layout
                  </TableCell>
                )}
                <TableCell
                  sx={{ fontWeight: 'bold', width: 120 }}
                  align="right"
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {modifierOptions.map((option) => {
                const item = option.itemData;
                const itemHasModifiers =
                  item?.modifierGroupIds && item.modifierGroupIds.length > 0;
                const isExpanded = expandedOptions[option.itemId];
                const values = editingValues[option.itemId] || {
                  name: getTranslationValue(item?.name) || '',
                  price: item?.price,
                  priceType: item?.priceType || null,
                  inOrderViewLayout: item?.inOrderViewLayout || null,
                };

                return (
                  <React.Fragment key={option.itemId}>
                    <TableRow
                      hover
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { bgcolor: '#f5f5f5' },
                      }}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <TextField
                            size="small"
                            value={values.name}
                            onChange={(e) =>
                              handleFieldChange(
                                option.itemId,
                                'name',
                                e.target.value,
                                values
                              )
                            }
                            onBlur={() => handleBlurSave(option, 'name')}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{
                              style: { fontSize: '0.875rem' },
                            }}
                            sx={{ flex: 1, minWidth: 150 }}
                          />
                          <FieldSavingIndicator
                            status={fieldSavingStatus[`${option.itemId}_name`]}
                          />
                          {itemHasModifiers && (
                            <Chip
                              label={`${item.modifierGroupIds.length} modifier(s)`}
                              size="small"
                              color="secondary"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <TextField
                            size="small"
                            type="text"
                            value={
                              values.price != null
                                ? formatNumber(values.price)
                                : ''
                            }
                            onChange={(e) => {
                              const transformed = priceTransform(
                                e.target.value
                              );
                              handleFieldChange(
                                option.itemId,
                                'price',
                                transformed,
                                values
                              );
                            }}
                            onBlur={() => handleBlurSave(option, 'price')}
                            InputLabelProps={{ shrink: true }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  {(() => {
                                    // Check option's priceType first, then parent MG's priceType
                                    const effectivePriceType =
                                      values.priceType ||
                                      modifierGroup?.priceType;
                                    return effectivePriceType &&
                                      effectivePriceType !== 'fixPrice'
                                      ? '×'
                                      : '$';
                                  })()}
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment position="end">
                                  <PosProviderPriceOverridePopover
                                    itemId={option.itemId}
                                    availableInKiosk={item?.availableInKiosk}
                                  />
                                </InputAdornment>
                              ),
                            }}
                            inputProps={{
                              style: { fontSize: '0.875rem' },
                            }}
                            sx={{ flex: 1 }}
                          />
                          <FieldSavingIndicator
                            status={fieldSavingStatus[`${option.itemId}_price`]}
                          />
                        </Box>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <FormControl size="small" sx={{ width: 140 }}>
                            <SearchableSelect
                              value={values.priceType || ''}
                              displayEmpty
                              renderValue={(selected) => {
                                const opt = PRICE_TYPE_OPTIONS.find(
                                  (o) => o.value === selected
                                );
                                return opt?.label || 'None';
                              }}
                              onChange={async (e) => {
                                const newValue = e.target.value || null;
                                const originalPriceType =
                                  option.itemData?.priceType;

                                // Update local state
                                handleFieldChange(
                                  option.itemId,
                                  'priceType',
                                  newValue,
                                  values
                                );

                                // Skip if no change
                                if (newValue === originalPriceType) return;

                                // Save directly instead of using handleBlurSave
                                try {
                                  setFieldStatus(
                                    option.itemId,
                                    'priceType',
                                    'saving'
                                  );
                                  await dispatch(
                                    updateItem(option.itemId, {
                                      provider: 'pos',
                                      priceType: newValue,
                                    })
                                  );
                                  setFieldStatus(
                                    option.itemId,
                                    'priceType',
                                    'saved'
                                  );
                                } catch (err) {
                                  setFieldStatus(
                                    option.itemId,
                                    'priceType',
                                    'error'
                                  );
                                  toast.error(
                                    `Failed to save price type: ${
                                      err.message || 'Unknown error'
                                    }`
                                  );
                                }
                              }}
                              sx={{ fontSize: '0.875rem' }}
                            >
                              {PRICE_TYPE_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </MenuItem>
                              ))}
                            </SearchableSelect>
                          </FormControl>
                          <FieldSavingIndicator
                            status={
                              fieldSavingStatus[`${option.itemId}_priceType`]
                            }
                          />
                        </Box>
                      </TableCell>
                      {showOrderViewLayoutColumn && (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {(values.priceType || modifierGroup?.priceType) === 'multiplierApplyingToParentPrice' ? (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <FormControl size="small" sx={{ width: 140 }}>
                              <SearchableSelect
                                value={values.inOrderViewLayout || ''}
                                displayEmpty
                                renderValue={(selected) => {
                                  const opt = IN_ORDER_VIEW_LAYOUT_OPTIONS.find(
                                    (o) => o.value === selected
                                  );
                                  return opt?.label || 'None';
                                }}
                                onChange={async (e) => {
                                  const newValue = e.target.value || null;
                                  const originalValue =
                                    option.itemData?.inOrderViewLayout || null;

                                  // Update local state
                                  handleFieldChange(
                                    option.itemId,
                                    'inOrderViewLayout',
                                    newValue,
                                    values
                                  );

                                  // Skip if no change
                                  if (newValue === originalValue) return;

                                  // Save directly
                                  try {
                                    setFieldStatus(
                                      option.itemId,
                                      'inOrderViewLayout',
                                      'saving'
                                    );
                                    await dispatch(
                                      updateItem(option.itemId, {
                                        provider: 'pos',
                                        inOrderViewLayout: newValue,
                                      })
                                    );
                                    setFieldStatus(
                                      option.itemId,
                                      'inOrderViewLayout',
                                      'saved'
                                    );
                                  } catch (err) {
                                    setFieldStatus(
                                      option.itemId,
                                      'inOrderViewLayout',
                                      'error'
                                    );
                                    toast.error(
                                      `Failed to save in-order view layout: ${
                                        err.message || 'Unknown error'
                                      }`
                                    );
                                  }
                                }}
                                sx={{ fontSize: '0.875rem' }}
                              >
                                {IN_ORDER_VIEW_LAYOUT_OPTIONS.map((opt) => (
                                  <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </MenuItem>
                                ))}
                              </SearchableSelect>
                            </FormControl>
                            <FieldSavingIndicator
                              status={
                                fieldSavingStatus[`${option.itemId}_inOrderViewLayout`]
                              }
                            />
                          </Box>
                        ) : (
                          <Typography
                            sx={{ fontSize: '0.75rem', color: '#9E9EAD' }}
                          >
                            —
                          </Typography>
                        )}
                      </TableCell>
                      )}
                      <TableCell align="right">
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 0.5,
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                          }}
                        >
                          <Box onClick={(e) => e.stopPropagation()}>
                            <AvailabilityControl
                              value={getAvailabilityStatus(
                                item,
                                currentBusiness
                              )}
                              onChange={(status) =>
                                handleOptionAvailabilityChange(option, status)
                              }
                              entityName={getTranslationValue(item?.name)}
                            />
                          </Box>
                          {itemHasModifiers && (
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleExpand(option.itemId);
                              }}
                              title={
                                isExpanded ? 'Collapse' : 'Expand modifiers'
                              }
                            >
                              {isExpanded ? (
                                <ExpandLessIcon fontSize="small" />
                              ) : (
                                <Badge
                                  badgeContent={item.modifierGroupIds.length}
                                  color="primary"
                                >
                                  <ExpandMoreIcon fontSize="small" />
                                </Badge>
                              )}
                            </IconButton>
                          )}
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleNavigateToItem(option.itemId);
                            }}
                            title="Open item editor"
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDuplicateOption(option);
                            }}
                            title="Duplicate option"
                            disabled={duplicatingOptionId === option.itemId}
                          >
                            {duplicatingOptionId === option.itemId ? (
                              <CircularProgress size={16} />
                            ) : (
                              <ContentCopyIcon fontSize="small" />
                            )}
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveOption(option);
                            }}
                            title="Remove from modifier group"
                            color="error"
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>

                    {/* Show nested modifier groups row */}
                    {itemHasModifiers && isExpanded && (
                      <TableRow>
                        <TableCell
                          colSpan={showOrderViewLayoutColumn ? 5 : 4}
                          sx={{ py: 0, bgcolor: '#f9f9f9' }}
                        >
                          <Collapse
                            in={isExpanded}
                            timeout="auto"
                            unmountOnExit
                          >
                            <Box sx={{ p: 2 }}>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  mb: 1,
                                  fontWeight: 'bold',
                                }}
                              >
                                <InfoIcon fontSize="small" />
                                This option has its own modifiers (recursive
                                structure):
                              </Typography>
                              <List dense>
                                {item.modifierGroupIds.map((mgId) => {
                                  const mg = menuMap?.modifierGroups?.[mgId];
                                  if (!mg) return null;

                                  const mgOptions = mg.options || [];
                                  return (
                                    <ListItem
                                      key={mgId}
                                      sx={{
                                        bgcolor: 'white',
                                        mb: 0.5,
                                        borderRadius: 1,
                                      }}
                                    >
                                      <ListItemText
                                        primary={getTranslationValue(mg.name)}
                                        secondary={
                                          <span>
                                            Min: {mg.min || 0} | Max:{' '}
                                            {mg.max || 0} | Free: {mg.free || 0}
                                            {mgOptions.length > 0 &&
                                              ` | ${mgOptions.length} option(s)`}
                                          </span>
                                        }
                                      />
                                    </ListItem>
                                  );
                                })}
                              </List>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        );
      })()}

      {/* Add Options Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '1.5rem',
            fontWeight: 600,
            pb: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          Add Options to &quot;{getTranslationValue(modifierGroup?.name)}&quot;
        </DialogTitle>
        <DialogContent sx={{ overflowX: 'hidden' }}>
          {!isCreatingNewItem ? (
            <Box>
              <Box sx={{ pt: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setIsCreatingNewItem(true)}
                  fullWidth
                  size="large"
                  sx={{
                    mb: 3,
                    py: 1.5,
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 600,
                    boxShadow: 2,
                    '&:hover': {
                      boxShadow: 3,
                    },
                  }}
                >
                  Create New Item
                </Button>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    my: 3,
                  }}
                >
                  <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 600,
                      letterSpacing: 1,
                    }}
                  >
                    OR SELECT EXISTING ({availableItems.length})
                  </Typography>
                  <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                </Box>
              </Box>

              {availableItems.length === 0 ? (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ fontStyle: 'italic', textAlign: 'center', py: 2 }}
                >
                  All items are already added to this modifier group.
                </Typography>
              ) : (
                <Box>
                  <TextField
                    fullWidth
                    size="medium"
                    placeholder="Search items..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'white',
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                      },
                    }}
                    autoFocus
                  />

                  {/* Selected items section at the top */}
                  {selectedItemsData.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="caption"
                        color="primary"
                        sx={{
                          display: 'block',
                          mb: 1,
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                        }}
                      >
                        {selectedItemsData.length} Selected
                      </Typography>
                      <List disablePadding sx={{ px: '10px' }}>
                        {selectedItemsData.map((item) =>
                          renderAvailableItem(item)
                        )}
                      </List>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          my: 2,
                        }}
                      >
                        <Box
                          sx={{ flex: 1, height: '1px', bgcolor: 'divider' }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            fontWeight: 500,
                          }}
                        >
                          Available ({unselectedFilteredItems.length})
                        </Typography>
                        <Box
                          sx={{ flex: 1, height: '1px', bgcolor: 'divider' }}
                        />
                      </Box>
                    </Box>
                  )}

                  {unselectedFilteredItems.length === 0 &&
                  selectedItemsData.length === 0 ? (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ fontStyle: 'italic', textAlign: 'center', py: 2 }}
                    >
                      {`No items found matching "${searchQuery}"`}
                    </Typography>
                  ) : unselectedFilteredItems.length > 0 ? (
                    <>
                      <Virtuoso
                        style={{
                          height: selectedItemsData.length > 0 ? 300 : 500,
                          width: '100%',
                        }}
                        data={displayedItems}
                        computeItemKey={(index, item) => item._id}
                        itemContent={(index, item) => renderAvailableItem(item)}
                        increaseViewportBy={{ top: 400, bottom: 400 }}
                        components={virtuosoComponents}
                        followOutput={false}
                        initialTopMostItemIndex={0}
                      />

                      {hasMore && (
                        <Box sx={{ textAlign: 'center', mt: 2 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={handleLoadMore}
                          >
                            Load More (
                            {unselectedFilteredItems.length -
                              displayedItems.length}{' '}
                            remaining)
                          </Button>
                        </Box>
                      )}

                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{ display: 'block', textAlign: 'center', mt: 2 }}
                      >
                        Showing {displayedItems.length} of{' '}
                        {unselectedFilteredItems.length}
                      </Typography>
                    </>
                  ) : null}
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ pt: 1 }}>
              <Typography
                variant="overline"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 600,
                  letterSpacing: 1,
                  display: 'block',
                  mb: 2,
                }}
              >
                New Item Details
              </Typography>

              <Box sx={{ mb: 2 }}>
                <TextField
                  label="Name"
                  fullWidth
                  value={newItemName}
                  onChange={(e) => setNewItemName(e.target.value)}
                  required
                  autoFocus
                  InputLabelProps={{ shrink: true }}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <TextField
                  label="Description"
                  fullWidth
                  value={newItemDescription}
                  onChange={(e) => setNewItemDescription(e.target.value)}
                  multiline
                  rows={2}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <TextField
                  label="Price"
                  type="text"
                  fullWidth
                  value={newItemPrice != null ? formatNumber(newItemPrice) : ''}
                  onChange={(e) => {
                    const transformed = priceTransform(e.target.value);
                    setNewItemPrice(transformed);
                  }}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {newItemPriceType && newItemPriceType !== 'fixPrice'
                          ? '×'
                          : '$'}
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel id="new-item-price-type-label">
                    Price Type
                  </InputLabel>
                  <SearchableSelect
                    labelId="new-item-price-type-label"
                    label="Price Type"
                    value={newItemPriceType || ''}
                    displayEmpty
                    renderValue={(selected) => {
                      const option = PRICE_TYPE_OPTIONS.find(
                        (o) => o.value === selected
                      );
                      return option?.label || 'None';
                    }}
                    onChange={(e) => {
                      const newValue = e.target.value || null;
                      setNewItemPriceType(newValue);
                    }}
                  >
                    {PRICE_TYPE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </SearchableSelect>
                </FormControl>
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    checked={newItemHideNameOnOrder}
                    onChange={(e) =>
                      setNewItemHideNameOnOrder(e.target.checked)
                    }
                  />
                }
                label={
                  <Box>
                    <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                      Hide Name on Order
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#9E9EAD' }}>
                      Hide the option name on the order receipt
                    </Typography>
                  </Box>
                }
                sx={{ mt: 1, mb: 2 }}
              />

              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
                  Nested Layout
                </Typography>
                <FormControl size="small" fullWidth>
                  <SearchableSelect
                    value={newItemShowNestedLayout}
                    onChange={(e) => setNewItemShowNestedLayout(e.target.value)}
                  >
                    <MenuItem value="default">Default</MenuItem>
                    <MenuItem value="popup">Popup</MenuItem>
                  </SearchableSelect>
                </FormControl>
                <Typography
                  sx={{ fontSize: '12px', color: '#9E9EAD', mt: 0.5 }}
                >
                  Choose how nested modifiers are displayed
                </Typography>
              </Box>

              {newItemPriceType === 'multiplierApplyingToParentPrice' && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}
                  >
                    In-Order View Layout
                  </Typography>
                  <FormControl size="small" fullWidth>
                    <SearchableSelect
                      value={newItemInOrderViewLayout || ''}
                      displayEmpty
                      renderValue={(selected) => {
                        const option = IN_ORDER_VIEW_LAYOUT_OPTIONS.find(
                          (o) => o.value === selected
                        );
                        return option?.label || 'None';
                      }}
                      onChange={(e) => {
                        const newValue = e.target.value || null;
                        setNewItemInOrderViewLayout(newValue);
                      }}
                    >
                      {IN_ORDER_VIEW_LAYOUT_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </SearchableSelect>
                  </FormControl>
                  <Typography
                    sx={{ fontSize: '12px', color: '#9E9EAD', mt: 0.5 }}
                  >
                    Controls how this option is displayed in the order view
                  </Typography>
                </Box>
              )}

              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 2,
                  p: 1.5,
                  bgcolor: '#e3f2fd',
                  borderRadius: 1,
                  color: 'primary.dark',
                }}
              >
                💡 The item will be created and automatically added as an option
                to this modifier group
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: '#fafafa',
          }}
        >
          {isCreatingNewItem && (
            <Button
              onClick={() => setIsCreatingNewItem(false)}
              sx={{ mr: 'auto', textTransform: 'none' }}
              disabled={isCreatingItem}
            >
              Back
            </Button>
          )}
          <Button
            onClick={handleCloseDialog}
            sx={{ textTransform: 'none' }}
            disabled={isCreatingItem || isAddingOptions}
          >
            Cancel
          </Button>
          {isCreatingNewItem ? (
            <Button
              onClick={handleCreateNewItem}
              variant="contained"
              color="primary"
              disabled={!newItemName.trim() || isCreatingItem}
              startIcon={
                isCreatingItem ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <AddIcon />
                )
              }
              sx={{
                textTransform: 'none',
                px: 3,
                boxShadow: 2,
                '&:hover': {
                  boxShadow: 3,
                },
              }}
            >
              {isCreatingItem ? 'Creating...' : 'Create & Add'}
            </Button>
          ) : (
            <Button
              onClick={handleAddOptions}
              variant="contained"
              color="primary"
              disabled={selectedItems.length === 0 || isAddingOptions}
              startIcon={
                isAddingOptions ? (
                  <CircularProgress size={20} color="inherit" />
                ) : null
              }
              sx={{
                textTransform: 'none',
                px: 3,
                boxShadow: 2,
                '&:hover': {
                  boxShadow: 3,
                },
              }}
            >
              {isAddingOptions
                ? 'Adding...'
                : `Add ${
                    selectedItems.length > 0 ? `(${selectedItems.length})` : ''
                  } Options`}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Sort Options Modal */}
      <OptionSortModal
        open={sortModalOpen}
        onClose={() => setSortModalOpen(false)}
        modifierGroup={modifierGroup}
        menuMap={menuMap}
      />
    </Box>
  );
};

export default ModifierGroupOptionsManager;
